import { useContext, useState, useEffect } from 'react';
import { Box, Grommet, Layer, Text, Button, ThemeType } from 'grommet';
import { Close, Link, ShareOption } from 'grommet-icons';
import QRCode from 'react-qr-code';
import { TeamContext, UserContext } from '@services/Session';
import Toast from '@components/Toast';
import { GROMMETTHEME } from '@constants/grommetTheme';
import { UI } from '@constants/baseUrls';
import axios from '@helpers/axios';

interface QrModal {
  onClose: () => void;
  source?: string | null;
}

const QrModal = ({ onClose, source = null }: QrModal) => {
  const userStore = useContext(UserContext);
  const team = useContext(TeamContext);
  const teamId = team.id;
  const isAdmin = team.isTeamManager;
  const referrerUserId = userStore.authUser
    ? userStore?.authUser?.claims?.shd_user_id
    : null;

  const qrCodeSize = 220;
  const [copied, setCopied] = useState(false);
  const [scoreLink, setScoreLink] = useState('');
  const [selectedTab, setSelectedTab] = useState('team');
  useEffect(() => {
    if (isAdmin && team) {
      axios
        .get(`/api/score_delegation_key/${teamId}`)
        .then(({ data: respScoreDelegationKey }) => {
          setScoreLink(
            `${UI}/${teamId}?tab=scoring&key=${respScoreDelegationKey}`
          );
        });
    }
  }, [isAdmin, team, teamId]);

  const getCurrentUrl = () => {
    if (selectedTab === 'score' && scoreLink) {
      return scoreLink.toString();
    }
    const url = new URL(`${UI}/${team.nameHandle}`);
    if (referrerUserId) {
      url.searchParams.append('from', referrerUserId);
    }
    url.searchParams.append('campaign', 'qr_scan');
    if (source) {
      url.searchParams.append('s', source);
    }
    return url.toString();
  };

  const getCurrentDisplayUrl = () => {
    return selectedTab === 'score' && scoreLink
      ? `sidelinehd.com/scoring/${teamId}`
      : `sidelinehd.com/${team.nameHandle}`;
  };

  const handleShare = () => {
    const url = getCurrentUrl();
    if (navigator.share) {
      navigator.share({
        title: `${team.nameMed} on sidelineHD`,
        text: `View ${team.nameMed} on sidelineHD`,
        url: url,
      });
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
    }
  };

  return (
    <Grommet
      theme={
        {
          ...GROMMETTHEME,
          layer: { background: 'transparent' },
        } as unknown as ThemeType
      }
    >
      <Layer
        onEsc={onClose}
        responsive={false}
        full="vertical"
        animation={false}
      >
        <Box fill="vertical" width="320px" justify="center" align="center">
          {scoreLink && (
            <Box
              direction="row"
              background="#F6F6F6"
              round="large"
              width="85%"
              justify="center"
              margin={{ bottom: 'large' }}
              style={{
                display: 'flex',
              }}
            >
              <Button
                plain
                onClick={() => setSelectedTab('team')}
                margin={{ right: '4px' }}
                style={{
                  backgroundColor:
                    selectedTab === 'team' ? '#FFFFFF' : '#F6F6F6',
                  boxShadow:
                    selectedTab === 'team'
                      ? '2px 0px 0px rgba(225, 225, 225, 1)'
                      : 'none',
                  color:
                    selectedTab === 'team' ? '#000000' : 'rgba(0, 0, 0, 0.5)',
                  padding: '10px 20px',
                  borderRadius: '16px',
                  flex: 1,
                  fontWeight: '600',
                  fontSize: '14px',
                  textAlign: 'center',
                  transition: 'all 0.2s ease-in-out',
                }}
                label="Team page"
              />
              <Button
                plain
                onClick={() => setSelectedTab('score')}
                style={{
                  backgroundColor:
                    selectedTab === 'score' ? '#FFFFFF' : '#F6F6F6',
                  boxShadow:
                    selectedTab === 'score'
                      ? '-2px 0px 0px rgba(225, 225, 225, 1)'
                      : 'none',
                  color:
                    selectedTab === 'score' ? '#000000' : 'rgba(0, 0, 0, 0.5)',
                  padding: '10px 20px',
                  borderRadius: '16px',
                  flex: 1,
                  fontWeight: '600',
                  fontSize: '14px',
                  textAlign: 'center',
                  transition: 'all 0.2s ease-in-out',
                }}
                label="Score link"
              />
            </Box>
          )}
          <Box align="center" gap="medium" margin={{ top: 'medium' }}>
            <Box
              align="center"
              justify="center"
              background="white"
              pad={{ horizontal: '30px', vertical: '30px' }}
              round="small"
              style={{
                borderRadius: '12px',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              }}
            >
              <QRCode size={qrCodeSize} value={getCurrentUrl()} />
            </Box>
            <Text
              weight="bold"
              size="large"
              color="white"
              style={{ textAlign: 'center' }}
            >
              {getCurrentDisplayUrl()}
            </Text>
            <Box
              direction="row"
              gap="medium"
              fill="horizontal"
              justify="center"
              margin={{ top: 'small' }}
            >
              <Button
                plain
                onClick={handleShare}
                style={{
                  backgroundColor: 'white',
                  padding: '10px 15px',
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  width: '130px',
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <ShareOption size="24px" />
                <Text
                  size="small"
                  weight="bold"
                  color="black"
                  margin={{ top: '12px' }}
                >
                  Share {selectedTab === 'score' ? 'Score Link' : 'Profile'}
                </Text>
              </Button>
              <Button
                plain
                onClick={() => {
                  navigator.clipboard.writeText(getCurrentUrl());
                  setCopied(true);
                }}
                style={{
                  backgroundColor: 'white',
                  padding: '10px 15px',
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  width: '130px',
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Link size="24px" />
                <Text
                  size="small"
                  weight="bold"
                  color="black"
                  margin={{ top: '12px' }}
                >
                  Copy Link
                </Text>
              </Button>
            </Box>
          </Box>
        </Box>
        <Box align="center" margin="medium">
          <Box
            background="white"
            round="full"
            pad="small"
            onClick={onClose}
            align="center"
            justify="center"
            style={{
              width: '56px',
              height: '56px',
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.2)',
            }}
          >
            <Close size="24px" />
          </Box>
        </Box>
        {copied && (
          <Toast
            label="Copied to clipboard!"
            background="status-ok"
            duration={3000}
            onClose={() => setCopied(false)}
          />
        )}
      </Layer>
    </Grommet>
  );
};

export default QrModal;
